import { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  CurrencyDollar, TrendUp, TrendDown, Wallet, Bank, Receipt, FileText, ShoppingCart,
  Stamp, Warning, CircleNotch, ClockCounterClockwise, ArrowUp, ArrowDown, Crown,
  Heartbeat, ChartBar, ChartLineUp, ChartPie
} from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { useCurrency } from '../../contexts/CompanyContext';
import {
  CashFlowTrendChart, PnLTrendChart, QuotesInvoicesTrendChart,
  SalesPipelineFunnelChart, ExpensesPieChart, AgingBarChart, TopCustomersBarChart
} from './DashboardCharts';
import DrilldownModal from './DrilldownModal';
import QuickTransactionActions from './QuickTransactionActions';
import { useFinanceDrill } from '../reports/useFinanceDrill';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_TONE = {
  draft: 'bg-zinc-500/10 text-zinc-600',
  pending: 'bg-blue-500/10 text-blue-600',
  pending_approval: 'bg-blue-500/10 text-blue-600',
  approved: 'bg-emerald-500/10 text-emerald-600',
  client_approved: 'bg-emerald-500/10 text-emerald-600',
  sent: 'bg-violet-500/10 text-violet-600',
  paid: 'bg-emerald-500/10 text-emerald-600',
  cancelled: 'bg-red-500/10 text-red-600',
  rejected: 'bg-red-500/10 text-red-600',
};

// Card with optional click-through. When `to` is provided, the whole card
// becomes a router Link with a subtle hover lift — making EVERY KPI drill into
// its source list/report (Sales → Invoices, P&L → Financial Reports, etc.).
const Card = ({ children, className = '', testid, to, onClick }) => {
  if (onClick) {
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={onClick}
        data-testid={testid}
        className={`bg-card border rounded-xl p-5 block transition hover:shadow-md hover:-translate-y-0.5 hover:border-primary/40 cursor-pointer ${className}`}
      >
        {children}
      </div>
    );
  }
  if (to) {
    return (
      <Link
        to={to}
        data-testid={testid}
        className={`bg-card border rounded-xl p-5 block transition hover:shadow-md hover:-translate-y-0.5 hover:border-primary/40 focus:outline-none focus:ring-2 focus:ring-primary/40 cursor-pointer ${className}`}
      >
        {children}
      </Link>
    );
  }
  return (
    <div data-testid={testid} className={`bg-card border rounded-xl p-5 ${className}`}>{children}</div>
  );
};

const SectionTitle = ({ icon: Icon, children, action }) => (
  <div className="flex items-center justify-between mb-3">
    <div className="flex items-center gap-2">
      {Icon && <Icon size={18} weight="duotone" className="text-primary" />}
      <h3 className="text-sm font-semibold tracking-tight">{children}</h3>
    </div>
    {action}
  </div>
);

const fmtPct = (v) => `${(v ?? 0).toFixed(1)}%`;

export default function DashboardOverview() {
  const { formatCurrency } = useCurrency();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resyncing, setResyncing] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  const [reconHealth, setReconHealth] = useState(null);
  const [needsCorrection, setNeedsCorrection] = useState(null);
  const [vatAudit, setVatAudit] = useState(null);
  const [vatRecomputing, setVatRecomputing] = useState(false);
  const [drilldown, setDrilldown] = useState(null);
  const { openDrill, openParty, dialogs } = useFinanceDrill();
  const now = new Date();
  const monthStart = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
  const todayIso = now.toISOString().split('T')[0];
  const lastDay = (nextMonthStart) => new Date(new Date(`${nextMonthStart}T00:00:00Z`).getTime() - 86400000).toISOString().split('T')[0];
  const drillMonth = (category, title, extra = {}) => openDrill({ category, title, startDate: monthStart, endDate: todayIso, ...extra });

  // Helpers to compute month start/end from "YYYY-MM" label
  const monthRange = (label) => {
    const [y, m] = (label || '').split('-').map(Number);
    if (!y || !m) return {};
    const from = `${y}-${String(m).padStart(2,'0')}-01`;
    const ny = m === 12 ? y + 1 : y;
    const nm = m === 12 ? 1 : m + 1;
    const to = `${ny}-${String(nm).padStart(2,'0')}-01`;
    return { from, to };
  };

  const fetchOverview = () => {
    setLoading(true);
    axios.get(`${API}/dashboard/overview`)
      .then(r => setD(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
    // Reconciliation health runs alongside the dashboard overview — silent on error
    axios.get(`${API}/reconciliation/health`)
      .then(r => setReconHealth(r.data))
      .catch(() => setReconHealth(null));
    // Vouchers that the reconciliation pass reset to draft and now need the
    // user to pick the correct expense/income account.
    axios.get(`${API}/vouchers/needs-correction`)
      .then(r => setNeedsCorrection(r.data))
      .catch(() => setNeedsCorrection(null));
    // VAT audit — surfaces documents whose vat_category is non-standard but
    // whose tax_total > 0 (a data scar left by docs created before the
    // backend safety net was added).
    axios.get(`${API}/vat/audit`)
      .then(r => setVatAudit(r.data))
      .catch(() => setVatAudit(null));
  };

  const handleVatRecompute = async () => {
    if (!window.confirm('Recompute totals on all VAT-inconsistent documents? Per-line tax_rate will be forced to 0 and totals will be set to subtotal - discount.')) return;
    setVatRecomputing(true);
    try {
      const r = await axios.post(`${API}/vat/recompute`, {});
      const f = r.data?.summary?.fixed_count || 0;
      const d = r.data?.summary?.delta_total || 0;
      if (f) toast.success(`Recomputed ${f} document(s) — total VAT removed: ${d.toLocaleString()}`);
      else toast.success('All documents already in sync.');
      fetchOverview();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Recompute failed');
    } finally {
      setVatRecomputing(false);
    }
  };

  // Auto-refresh dashboard every 60 seconds so KPIs stay live.
  // Skip when document is hidden (battery-friendly).
  useEffect(() => {
    const onVisChange = () => { if (!document.hidden) fetchOverview(); };
    document.addEventListener('visibilitychange', onVisChange);
    const iv = setInterval(() => { if (!document.hidden) fetchOverview(); }, 60000);
    return () => { clearInterval(iv); document.removeEventListener('visibilitychange', onVisChange); };
  }, []);

  useEffect(() => { fetchOverview(); }, []);

  const handleReconcile = async () => {
    if (!window.confirm(
      'Reconcile All Finance Data?\n\n' +
      'This will:\n' +
      '• Auto-correct receipt vouchers with the WRONG payer (e.g. voucher linked to INV-0004/INV-0026 but paid-by field shows a different customer).\n' +
      '• Auto-correct payment vouchers with the WRONG payee (payables side of the same bug).\n' +
      '• Auto-link orphan receipts to matching outstanding invoices.\n' +
      '• Recompute every invoice\'s amount_paid / balance_due from its active Receipt Vouchers.\n' +
      '• Revert any invoice marked PAID whose receipt voucher was deleted, so you can re-record the payment.\n' +
      '• Re-post any missing Journal Entries for approved invoices and receipts.\n' +
      '• Void Journal Entries left behind by deleted vouchers.\n\n' +
      'It is safe and idempotent. Continue?'
    )) return;
    setReconciling(true);
    try {
      // Comprehensive accounting audit + heal — runs the full chain:
      //  1. Receipt voucher payer mismatch fix (INV-0026, INV-0004 bug)
      //  2. Payment voucher payee mismatch fix (payables side)
      //  3. Auto-link orphan receipts to matching invoices
      //  4. Reconcile every invoice's amount_paid from active vouchers
      let auditReport = null;
      let mismatchFixes = 0;
      let payeeFixes = 0;
      let relinked = 0;
      let orphansLinked = 0;
      let orphansPending = 0;
      try {
        const audit = await axios.post(`${API}/vouchers/accounting/full-audit-heal`);
        auditReport = audit.data || {};
        mismatchFixes = auditReport.summary?.receipt_payer_fixes || 0;
        payeeFixes = auditReport.summary?.payment_payee_fixes || 0;
        relinked = auditReport.summary?.receipts_relinked_from_proforma_or_quotation || 0;
        orphansLinked = auditReport.summary?.orphans_auto_linked || 0;
        orphansPending = auditReport.summary?.orphans_still_needing_manual_link || 0;
      } catch (_) { /* non-fatal — fall through to legacy reconcile */ }

      const res = await axios.post(`${API}/reconciliation/reconcile`);
      const r = res.data || {};
      const reverted = (r.invoices_reverted_to_approved || []).length;
      const recomputed = (r.invoices_recomputed || []).length;
      const invJe = (r.invoice_journal_entries_created || []).length;
      const rvJe = (r.receipt_journal_entries_created || []).length;
      const voided = (r.journal_entries_voided || []).length;
      const corrections = (r.vouchers_needing_correction || []).length;
      const skipped = (r.skipped || []).length;
      const parts = [];
      if (mismatchFixes) parts.push(`${mismatchFixes} receipt(s) with wrong payer corrected`);
      if (payeeFixes) parts.push(`${payeeFixes} payment(s) with wrong payee corrected`);
      if (relinked) parts.push(`${relinked} receipt(s) re-linked from Proforma/Quotation to Tax Invoice`);
      if (orphansLinked) parts.push(`${orphansLinked} orphan receipt(s) auto-linked to invoices`);
      if (recomputed) parts.push(`${recomputed} invoice(s) recomputed`);
      if (invJe) parts.push(`${invJe} invoice JE(s) created`);
      if (rvJe) parts.push(`${rvJe} receipt JE(s) created`);
      if (voided) parts.push(`${voided} stale JE(s) voided`);
      if (reverted) parts.push(`${reverted} invoice(s) reverted to approved (re-record their payment)`);
      if (corrections) parts.push(`${corrections} voucher(s) reset to draft — open Vouchers page to pick the correct expense/income account`);
      if (orphansPending) parts.push(`${orphansPending} orphan receipt(s) still need manual invoice-link (open Vouchers page)`);
      const msg = parts.length === 0 ? 'All finance data already in sync' : parts.join(' · ');
      if (skipped > 0) toast.warning(msg + ` · ${skipped} item(s) need manual review`);
      else if (corrections > 0) toast.warning(msg, { duration: 8000 });
      else toast.success(msg);
      fetchOverview();
    } catch (err) {
      const raw = err?.response?.data?.detail;
      let msg = 'Reconciliation failed';
      if (typeof raw === 'string') msg = raw;
      else if (raw && typeof raw === 'object') msg = raw.message || raw.code || JSON.stringify(raw);
      else if (err?.message) msg = err.message;
      toast.error(msg);
    } finally {
      setReconciling(false);
    }
  };

  const handleResync = async () => {
    if (!window.confirm(
      'Re-sync Ledger?\n\nThis will scan approved vouchers missing a Journal Entry and back-fill them. ' +
      'It is idempotent and safe — vouchers already linked are skipped.'
    )) return;
    setResyncing(true);
    try {
      const res = await axios.post(`${API}/vouchers/heal/backfill-journal-entries`);
      const t = res.data?.total_healed || 0;
      const cleaned = res.data?.total_cleaned || 0;
      const failed = res.data?.total_skipped_failures || 0;
      const parts = [];
      if (t > 0) parts.push(`Re-synced ${t} voucher(s)`);
      if (cleaned > 0) parts.push(`cleaned ${cleaned} legacy entry/entries`);
      if (failed > 0) parts.push(`${failed} could not be healed automatically`);
      if (parts.length === 0) {
        toast.success('All vouchers were already synced');
      } else if (failed === 0) {
        toast.success(parts.join(' · '));
      } else {
        toast.warning(parts.join(' · ') + ' — open Approvals for the detailed report');
      }
      fetchOverview();
    } catch (err) {
      // err.response.data.detail can be a string OR an object ({code, message, ...}).
      // toast.error() must receive a string — otherwise sonner throws and the page hangs.
      const raw = err?.response?.data?.detail;
      let msg = 'Re-sync failed';
      if (typeof raw === 'string') msg = raw;
      else if (raw && typeof raw === 'object') msg = raw.message || raw.code || JSON.stringify(raw);
      else if (err?.message) msg = err.message;
      toast.error(msg);
    } finally {
      setResyncing(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64"><CircleNotch size={32} className="animate-spin text-primary" /></div>
  );
  if (!d) return null;

  const sp = d.sales_pipeline || {};
  const pp = d.purchases_pipeline || {};
  const mf = d.money_flow || {};
  const bal = d.balances || {};
  const aging = d.aging || {};
  const pa = d.pending_actions || {};
  const tp = d.top_parties || {};
  const pnl = d.pnl || {};
  const pnlCur = pnl.current_month || {};
  const pnlPrev = pnl.previous_month || {};
  const profitDelta = pnlCur.net_profit - (pnlPrev.net_profit || 0);
  // Cash-basis numbers for the secondary line under each P&L card
  const rmonth = mf.receipts_this_month || { value: 0, count: 0 };
  const pmonth = mf.payments_this_month || { value: 0, count: 0 };

  const sumBucketCount = (b, statuses) => statuses.reduce((s, k) => s + (b?.[k]?.count || 0), 0);
  const sumBucketValue = (b, statuses) => statuses.reduce((s, k) => s + (b?.[k]?.value || 0), 0);

  const health = d.health || {};
  const orphans = (health.orphan_payment_vouchers || 0) + (health.orphan_receipt_vouchers || 0);
  const healthIssues = orphans + (health.unbalanced_journal_entries || 0);
  const trend6m = d.trend_6m || [];

  return (
    <div className="space-y-6" data-testid="dashboard-overview">
      {/* === HEALTH CHECK BANNER === */}
      {healthIssues > 0 ? (
        <motion.div
          initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
          className="bg-amber-500/10 border border-amber-500/40 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3"
          data-testid="health-banner"
        >
          <div className="flex items-start gap-3">
            <Heartbeat size={26} weight="duotone" className="text-amber-600 mt-0.5 shrink-0" />
            <div>
              <div className="font-semibold text-sm">Accounting Health: Action Needed</div>
              <ul className="text-xs text-muted-foreground mt-1 space-y-0.5">
                {orphans > 0 && (
                  <li>• <span className="font-medium text-amber-700">{orphans}</span> approved voucher(s) are missing a Journal Entry — ledger balances may be incorrect</li>
                )}
                {(health.unbalanced_journal_entries || 0) > 0 && (
                  <li>• <span className="font-medium text-amber-700">{health.unbalanced_journal_entries}</span> posted journal entries are unbalanced (Dr ≠ Cr)</li>
                )}
              </ul>
            </div>
          </div>
          {orphans > 0 && (
            <button
              onClick={handleResync}
              disabled={resyncing}
              data-testid="health-resync-btn"
              className="bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold px-4 py-2 rounded-lg transition flex items-center gap-2 disabled:opacity-60 shrink-0"
            >
              {resyncing ? <CircleNotch className="animate-spin" size={16} /> : <Heartbeat size={16} />}
              {resyncing ? 'Re-syncing…' : 'Re-sync now'}
            </button>
          )}
        </motion.div>
      ) : (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          className="bg-emerald-500/5 border border-emerald-500/30 rounded-xl px-4 py-2.5 flex items-center gap-3"
          data-testid="health-banner-ok"
        >
          <Heartbeat size={18} weight="duotone" className="text-emerald-600" />
          <span className="text-xs text-emerald-700 dark:text-emerald-400">
            Accounting health: all approved vouchers are linked to balanced Journal Entries.
          </span>
        </motion.div>
      )}

      {/* === VAT INCONSISTENCY BANNER === */}
      {/* Documents whose `vat_category` is non-standard but whose stored
          tax_total > 0 — leftover scars from before the backend safety net
          existed. Recomputing brings them in line with the displayed VAT. */}
      {vatAudit && vatAudit.summary?.total > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
          className="bg-purple-500/10 border border-purple-500/40 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3"
          data-testid="vat-inconsistent-banner"
        >
          <div className="flex items-start gap-3">
            <Warning size={26} weight="duotone" className="text-purple-600 mt-0.5 shrink-0" />
            <div>
              <div className="font-semibold text-sm">
                {vatAudit.summary.total} document{vatAudit.summary.total === 1 ? '' : 's'} have stale VAT totals
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                Their VAT Category is <span className="font-semibold">non-standard</span> (zero-rated / exempt / out-of-scope / not-yet-due) but the stored total still includes 5% VAT.
                {vatAudit.summary.safe > 0 && <> <span className="text-purple-700 font-semibold">{vatAudit.summary.safe}</span> safe to auto-fix (will remove <span className="font-mono">{vatAudit.summary.delta_sum?.toLocaleString()}</span> total).</>}
                {vatAudit.summary.blocked > 0 && <> <span className="text-rose-700 font-semibold">{vatAudit.summary.blocked}</span> need manual review (paid or terminal).</>}
              </div>
              <ul className="text-xs text-muted-foreground mt-2 space-y-0.5 max-h-24 overflow-y-auto">
                {vatAudit.entries.slice(0, 5).map(e => (
                  <li key={e.doc_id}>
                    • <span className="font-mono">{e.document_number || e.doc_id.slice(0, 8)}</span> [{e.label}] — currently <span className="line-through text-rose-600">{e.current_total.toLocaleString()}</span> → <span className="text-emerald-700 font-semibold">{e.new_total.toLocaleString()}</span>
                  </li>
                ))}
                {vatAudit.entries.length > 5 && (
                  <li className="italic">+ {vatAudit.entries.length - 5} more</li>
                )}
              </ul>
            </div>
          </div>
          <Button
            size="sm"
            disabled={vatRecomputing || vatAudit.summary.safe === 0}
            onClick={handleVatRecompute}
            className="bg-purple-600 hover:bg-purple-700 text-white shrink-0 self-start sm:self-center"
            data-testid="vat-recompute-btn"
          >
            {vatRecomputing ? 'Recomputing…' : `Recompute ${vatAudit.summary.safe} doc${vatAudit.summary.safe === 1 ? '' : 's'} →`}
          </Button>
        </motion.div>
      )}

      {/* === VOUCHERS NEEDING ACCOUNT CORRECTION === */}
      {/* These were auto-reset to draft by reconciliation. They will NOT show up
          on the P&L until the user fixes the line account and re-approves. */}
      {needsCorrection && needsCorrection.count > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
          className="bg-amber-500/10 border border-amber-500/40 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3"
          data-testid="needs-correction-banner"
        >
          <div className="flex items-start gap-3">
            <Warning size={26} weight="duotone" className="text-amber-600 mt-0.5 shrink-0" />
            <div>
              <div className="font-semibold text-sm">
                {needsCorrection.count} voucher{needsCorrection.count === 1 ? '' : 's'} need{needsCorrection.count === 1 ? 's' : ''} account correction
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                These drafts were auto-reset by the reconciliation pass. Until the line account is changed from the bank to a real Expense/Income account and re-approved, the amount will <span className="font-semibold text-amber-700">NOT appear on the P&amp;L</span>.
              </div>
              <ul className="text-xs text-muted-foreground mt-2 space-y-0.5 max-h-24 overflow-y-auto">
                {needsCorrection.vouchers.slice(0, 5).map(v => (
                  <li key={v.id}>
                    • <span className="font-mono">{v.voucher_number}</span> — {v.counterparty || '—'} — <span className="text-amber-700">{(v.total_amount || 0).toLocaleString()}</span>
                  </li>
                ))}
                {needsCorrection.vouchers.length > 5 && (
                  <li className="italic">+ {needsCorrection.vouchers.length - 5} more</li>
                )}
              </ul>
            </div>
          </div>
          <Link
            to="/dashboard/vouchers?needs_correction=1"
            className="bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold px-4 py-2 rounded-lg transition flex items-center gap-2 shrink-0 self-start sm:self-center"
            data-testid="needs-correction-fix-btn"
          >
            Fix Now →
          </Link>
        </motion.div>
      )}

      {/* === RECONCILIATION BANNER — surfaces drift between invoices, vouchers and ledger === */}
      {reconHealth && (
        reconHealth.healthy ? (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            className="bg-emerald-500/5 border border-emerald-500/30 rounded-xl px-4 py-2.5 flex items-center justify-between gap-3"
            data-testid="reconciliation-banner-ok"
          >
            <div className="flex items-center gap-3">
              <ChartBar size={18} weight="duotone" className="text-emerald-600" />
              <span className="text-xs text-emerald-700 dark:text-emerald-400">
                Finance reconciliation: all invoices and vouchers are in sync with the ledger.
              </span>
            </div>
            <button
              onClick={handleReconcile}
              disabled={reconciling}
              data-testid="reconciliation-run-btn-manual"
              title="Force a full audit even if no drift was auto-detected. Use this if a specific invoice's Statement of Accounts is still missing a payment (e.g. voucher linked to a Proforma/Quotation instead of the tax invoice)."
              className="text-[11px] font-semibold text-emerald-700 dark:text-emerald-400 border border-emerald-500/40 hover:bg-emerald-500/10 rounded-md px-2.5 py-1 transition disabled:opacity-60 shrink-0"
            >
              {reconciling ? 'Reconciling…' : 'Run audit anyway'}
            </button>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
            className="bg-rose-500/10 border border-rose-500/40 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3"
            data-testid="reconciliation-banner"
          >
            <div className="flex items-start gap-3">
              <Warning size={26} weight="duotone" className="text-rose-600 mt-0.5 shrink-0" />
              <div>
                <div className="font-semibold text-sm">Finance Reconciliation: {reconHealth.total_issues} issue(s) detected</div>
                <ul className="text-xs text-muted-foreground mt-1 space-y-0.5">
                  {(reconHealth.issues?.invoices_paid_mismatch || 0) > 0 && (
                    <li>• <span className="font-medium text-rose-700">{reconHealth.issues.invoices_paid_mismatch}</span> invoice(s) have amount_paid out of sync with their receipt vouchers</li>
                  )}
                  {(reconHealth.issues?.invoices_paid_without_rv || 0) > 0 && (
                    <li>• <span className="font-medium text-rose-700">{reconHealth.issues.invoices_paid_without_rv}</span> invoice(s) marked PAID but their receipt voucher was deleted</li>
                  )}
                  {(reconHealth.issues?.approved_invoices_missing_je || 0) > 0 && (
                    <li>• <span className="font-medium text-rose-700">{reconHealth.issues.approved_invoices_missing_je}</span> approved invoice(s) have no Journal Entry — Revenue/AR may be understated</li>
                  )}
                  {(reconHealth.issues?.approved_receipts_missing_je || 0) > 0 && (
                    <li>• <span className="font-medium text-rose-700">{reconHealth.issues.approved_receipts_missing_je}</span> approved receipt voucher(s) have no Journal Entry — Bank balance may be understated</li>
                  )}
                  {(reconHealth.issues?.deleted_receipts_with_live_je || 0) > 0 && (
                    <li>• <span className="font-medium text-rose-700">{reconHealth.issues.deleted_receipts_with_live_je}</span> deleted receipt(s) still have a posted Journal Entry — must be voided</li>
                  )}
                  {(reconHealth.issues?.self_cancelling_journal_entries || 0) > 0 && (
                    <li>• <span className="font-medium text-rose-700">{reconHealth.issues.self_cancelling_journal_entries}</span> journal entr{reconHealth.issues.self_cancelling_journal_entries === 1 ? 'y' : 'ies'} debit and credit the same account (zero net effect) — voucher will be reset to draft for correction</li>
                  )}
                </ul>
              </div>
            </div>
            <button
              onClick={handleReconcile}
              disabled={reconciling}
              data-testid="reconciliation-run-btn"
              className="bg-rose-500 hover:bg-rose-600 text-white text-sm font-semibold px-4 py-2 rounded-lg transition flex items-center gap-2 disabled:opacity-60 shrink-0"
            >
              {reconciling ? <CircleNotch className="animate-spin" size={16} /> : <ChartBar size={16} />}
              {reconciling ? 'Reconciling…' : 'Reconcile Now'}
            </button>
          </motion.div>
        )
      )}

      {/* === QUICK ACTIONS === */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xs uppercase font-semibold tracking-wider text-muted-foreground">Quick Actions</h3>
          <span className="text-[10px] text-muted-foreground hidden md:inline">
            Tip: press <kbd className="px-1.5 py-0.5 rounded bg-muted border font-mono text-[10px]">?</kbd> for keyboard shortcuts
          </span>
        </div>
        <QuickTransactionActions />
      </div>

      {/* === ROW 1 — Pending Actions === */}
      <motion.div initial={{opacity:0,y:6}} animate={{opacity:1,y:0}} className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Link to="/dashboard/approvals" className="contents">
          <Card testid="kpi-approvals-for-me" className="hover:border-primary/40 transition cursor-pointer">
            <div className="flex items-center gap-3">
              <Stamp size={28} weight="duotone" className="text-amber-500" />
              <div>
                <div className="text-2xl font-bold">{pa.approvals_for_me || 0}</div>
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Approvals for me</div>
              </div>
            </div>
          </Card>
        </Link>
        <Link to="/dashboard/sales" className="contents">
          <Card testid="kpi-draft-quotes" className="hover:border-primary/40 transition cursor-pointer">
            <div className="flex items-center gap-3">
              <FileText size={28} weight="duotone" className="text-blue-500" />
              <div>
                <div className="text-2xl font-bold">{pa.draft_quotes || 0}</div>
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Draft quotes</div>
              </div>
            </div>
          </Card>
        </Link>
        <Link to="/dashboard/vouchers" className="contents">
          <Card testid="kpi-pending-vouchers" className="hover:border-primary/40 transition cursor-pointer">
            <div className="flex items-center gap-3">
              <Receipt size={28} weight="duotone" className="text-violet-500" />
              <div>
                <div className="text-2xl font-bold">{pa.pending_vouchers || 0}</div>
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Pending vouchers</div>
              </div>
            </div>
          </Card>
        </Link>
        <Link to="/dashboard/sales" className="contents">
          <Card testid="kpi-overdue" className={`hover:border-primary/40 transition cursor-pointer ${pa.overdue_invoices ? 'border-red-500/40' : ''}`}>
            <div className="flex items-center gap-3">
              <Warning size={28} weight="duotone" className={pa.overdue_invoices ? 'text-red-500' : 'text-muted-foreground'} />
              <div>
                <div className="text-2xl font-bold">{pa.overdue_invoices || 0}</div>
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Overdue invoices</div>
              </div>
            </div>
          </Card>
        </Link>
      </motion.div>

      {/* === ROW 2 — P&L Snapshot ===
          ACCRUAL basis: revenue = SUM(credit-debit) on income accounts whose JE date is in the current month.
          Cash basis is shown as a secondary line under each card so the user can see BOTH views at a glance. */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <Card testid="pnl-revenue" onClick={() => drillMonth('income', 'Revenue Invoiced (this month) — income entries before VAT')}>
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground flex items-center gap-1" title="Accrual basis: income journal lines dated this month (before VAT). Click for the entries behind it.">
            Revenue Invoiced (this month)
          </div>
          <div className="text-2xl font-bold mt-1" data-testid="pnl-revenue-value">{formatCurrency(pnlCur.revenue)}</div>
          <div className="text-xs text-muted-foreground mt-1">vs {formatCurrency(pnlPrev.revenue)} last month</div>
          <div className="text-[11px] text-emerald-700 dark:text-emerald-400 mt-2 pt-2 border-t border-emerald-500/20 flex items-center gap-1" title="Cash basis: money into Cash & Bank accounts this month (journal lines).">
            <span className="font-semibold">Cash received:</span> {formatCurrency(rmonth.value)}
          </div>
        </Card>
        <Card testid="pnl-expenses" onClick={() => drillMonth('expense', 'Expenses (this month)')}>
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground" title="Accrual basis: expense journal lines dated this month (before VAT). Click for the entries behind it.">
            Expenses (this month)
          </div>
          <div className="text-2xl font-bold mt-1" data-testid="pnl-expenses-value">{formatCurrency(pnlCur.expenses)}</div>
          <div className="text-xs text-muted-foreground mt-1">vs {formatCurrency(pnlPrev.expenses)} last month</div>
          <div className="text-[11px] text-rose-700 dark:text-rose-400 mt-2 pt-2 border-t border-rose-500/20 flex items-center gap-1" title="Cash basis: money out of Cash & Bank accounts this month (journal lines).">
            <span className="font-semibold">Cash paid:</span> {formatCurrency(pmonth.value)}
          </div>
        </Card>
        <Card testid="pnl-net" onClick={() => drillMonth('pnl', 'Net Profit (this month) — revenue less expenses')} className={pnlCur.net_profit < 0 ? 'border-red-500/40' : ''}>
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground" title="Accrual basis net profit = Revenue − Expenses for the current month. Click for the entries.">
            Net Profit (Accrual)
          </div>
          <div className={`text-2xl font-bold mt-1 ${pnlCur.net_profit < 0 ? 'text-red-500' : 'text-emerald-600'}`}>
            {formatCurrency(pnlCur.net_profit)}
          </div>
          <div className={`text-xs mt-1 flex items-center gap-1 ${profitDelta >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
            {profitDelta >= 0 ? <TrendUp size={12} /> : <TrendDown size={12} />}
            {formatCurrency(Math.abs(profitDelta))} vs last month
          </div>
          <div className={`text-[11px] mt-2 pt-2 border-t border-muted/40 flex items-center gap-1 ${(rmonth.value - pmonth.value) >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-rose-700 dark:text-rose-400'}`} title="Cash basis net = money in − money out of Cash & Bank this month.">
            <span className="font-semibold">Net Cash:</span> {formatCurrency((rmonth.value || 0) - (pmonth.value || 0))}
          </div>
        </Card>
        <Card testid="pnl-margin" onClick={() => drillMonth('pnl', 'Margin % (this month)', { explain: `Margin = Net Profit ÷ Revenue × 100 = ${formatCurrency(pnlCur.net_profit)} ÷ ${formatCurrency(pnlCur.revenue)} × 100. Income rows add, expense rows subtract.` })}>
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground" title="(Revenue − Expenses) ÷ Revenue × 100. Click for the entries.">
            Margin % (Accrual)
          </div>
          <div className="text-2xl font-bold mt-1">{fmtPct(pnlCur.margin_pct)}</div>
          <div className="text-xs text-muted-foreground mt-1">last: {fmtPct(pnlPrev.margin_pct)}</div>
        </Card>
      </div>

      {/* === ROW 3 — Sales Pipeline + Purchases === */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card testid="sales-pipeline" to="/dashboard/sales">
          <SectionTitle icon={CurrencyDollar} action={<span className="text-xs text-primary">View all →</span>}>
            Sales Pipeline ({sp.total_quotes || 0} quotes)
          </SectionTitle>
          <div className="grid grid-cols-3 gap-2 text-center text-xs">
            {['draft','pending_approval','approved','sent','client_approved','closed'].map(s => (
              <div key={s} className={`rounded-lg p-2 ${STATUS_TONE[s] || 'bg-muted/30'}`}>
                <div className="font-bold text-base">{sp.buckets?.[s]?.count || 0}</div>
                <div className="capitalize text-[10px]">{s.replace('_',' ')}</div>
                <div className="text-[10px] text-muted-foreground">{formatCurrency(sp.buckets?.[s]?.value || 0)}</div>
              </div>
            ))}
          </div>
          <div className="mt-3 pt-3 border-t flex justify-between items-center">
            <span className="text-xs text-muted-foreground">Conversion to invoice</span>
            <span className="text-sm font-bold text-primary">{sp.converted_count || 0} / {sp.total_quotes || 0} ({fmtPct(sp.conversion_rate_pct)})</span>
          </div>
        </Card>

        <Card testid="purchases-pipeline" to="/dashboard/purchases">
          <SectionTitle icon={ShoppingCart} action={<span className="text-xs text-primary">View all →</span>}>
            Purchases ({pp.total_pos || 0} POs, {pp.rfq_count || 0} RFQs)
          </SectionTitle>
          <div className="grid grid-cols-3 gap-2 text-center text-xs">
            {['draft','pending_approval','approved','received','partial','closed'].map(s => (
              <div key={s} className={`rounded-lg p-2 ${STATUS_TONE[s] || 'bg-muted/30'}`}>
                <div className="font-bold text-base">{pp.po_buckets?.[s]?.count || 0}</div>
                <div className="capitalize text-[10px]">{s.replace('_',' ')}</div>
                <div className="text-[10px] text-muted-foreground">{formatCurrency(pp.po_buckets?.[s]?.value || 0)}</div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* === ROW 4 — Money in / out === */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card testid="money-flow" onClick={() => drillMonth('cash', 'Cash & Bank movements (this month)')}>
          <SectionTitle icon={Wallet}>Cash Flow (this month)</SectionTitle>
          <div className="space-y-3">
            <div className="flex justify-between items-center bg-emerald-500/5 rounded-lg p-3">
              <div className="flex items-center gap-2">
                <ArrowDown weight="bold" className="text-emerald-600" />
                <div>
                  <div className="text-xs text-muted-foreground">Receipts</div>
                  <div className="text-xs">money into Cash &amp; Bank</div>
                </div>
              </div>
              <div className="text-emerald-600 font-bold">{formatCurrency(mf.receipts_this_month?.value || 0)}</div>
            </div>
            <div className="flex justify-between items-center bg-red-500/5 rounded-lg p-3">
              <div className="flex items-center gap-2">
                <ArrowUp weight="bold" className="text-red-500" />
                <div>
                  <div className="text-xs text-muted-foreground">Payments</div>
                  <div className="text-xs">money out of Cash &amp; Bank</div>
                </div>
              </div>
              <div className="text-red-500 font-bold">{formatCurrency(mf.payments_this_month?.value || 0)}</div>
            </div>
            <div className={`flex justify-between items-center rounded-lg p-3 border ${mf.net_cash_flow_this_month < 0 ? 'border-red-500/40' : 'border-emerald-500/40'}`}>
              <span className="text-xs font-semibold">Net flow</span>
              <span className={`font-bold ${mf.net_cash_flow_this_month < 0 ? 'text-red-500' : 'text-emerald-600'}`}>
                {formatCurrency(mf.net_cash_flow_this_month || 0)}
              </span>
            </div>
          </div>
        </Card>

        <Card testid="account-balances" onClick={() => openDrill({ category: 'cash', title: 'Cash & Bank balances — all movements', endDate: todayIso })}>
          <SectionTitle icon={Bank} action={<span className="text-xs text-primary">Details →</span>}>Account Balances</SectionTitle>
          <div className="space-y-1.5 text-sm">
            {(bal.cash_bank || []).length === 0 ? (
              <p className="text-xs text-muted-foreground">No cash/bank accounts found</p>
            ) : (bal.cash_bank || []).map(a => (
              <div key={a.account_id} className="flex justify-between items-center py-1 border-b last:border-0">
                <span className="truncate">{a.account_name}</span>
                <span className={`font-bold ${a.is_negative ? 'text-red-500' : ''}`}>{formatCurrency(a.balance)}</span>
              </div>
            ))}
            <div className="flex justify-between pt-2 mt-2 border-t text-xs">
              <span className="text-muted-foreground">Receivables (customer ledgers)</span>
              <span className="font-semibold" data-testid="dash-receivables-subledger">{formatCurrency(bal.receivables_subledger ?? bal.accounts_receivable)}</span>
            </div>
            {Math.abs(bal.receivables_difference || 0) > 1 && (
              <div className="flex justify-between text-[11px] text-amber-700 dark:text-amber-400" data-testid="dash-receivables-diff">
                <span>Books (AR account) differ by</span>
                <span className="font-semibold">{formatCurrency(bal.receivables_difference)}</span>
              </div>
            )}
            <div className="flex justify-between text-xs">
              <span className="text-muted-foreground">Payables</span>
              <span className="font-semibold">{formatCurrency(bal.accounts_payable)}</span>
            </div>
          </div>
        </Card>

        <Card testid="top-expenses" onClick={() => drillMonth('expense', 'Expenses (this month)')}>
          <SectionTitle icon={TrendDown}>Top Expenses (this month)</SectionTitle>
          {(mf.top_expenses || []).length === 0 ? (
            <p className="text-xs text-muted-foreground">No expenses recorded yet</p>
          ) : (
            <ul className="space-y-2 text-sm">
              {(mf.top_expenses || []).map((e, i) => (
                <li key={i} className="flex justify-between border-b last:border-0 pb-1">
                  <span className="truncate">{e.account_code} {e.account_name}</span>
                  <span className="font-bold">{formatCurrency(e.total)}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      {/* === ROW 5 — Aging === */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card testid="ar-aging" onClick={() => openParty('customer')}>
          <SectionTitle icon={ClockCounterClockwise}>Receivables Aging (Customer)</SectionTitle>
          <div className="grid grid-cols-5 gap-1 text-center text-xs">
            {[
              ['current','Current'],['1_30','1-30'],['31_60','31-60'],['61_90','61-90'],['90_plus','90+']
            ].map(([k,label],i) => (
              <div key={k} className={`rounded-lg p-2 ${i>=2?'bg-red-500/10 text-red-600':'bg-muted/30'}`}>
                <div className="font-bold">{aging.receivable?.[k]?.count || 0}</div>
                <div className="text-[10px] text-muted-foreground">{label} d</div>
                <div className="text-[10px] break-words leading-tight">{formatCurrency(aging.receivable?.[k]?.value || 0)}</div>
              </div>
            ))}
          </div>
        </Card>
        <Card testid="ap-aging" onClick={() => openParty('supplier')}>
          <SectionTitle icon={ClockCounterClockwise}>Payables Aging (Supplier)</SectionTitle>
          <div className="grid grid-cols-5 gap-1 text-center text-xs">
            {[
              ['current','Current'],['1_30','1-30'],['31_60','31-60'],['61_90','61-90'],['90_plus','90+']
            ].map(([k,label],i) => (
              <div key={k} className={`rounded-lg p-2 ${i>=2?'bg-amber-500/10 text-amber-700':'bg-muted/30'}`}>
                <div className="font-bold">{aging.payable?.[k]?.count || 0}</div>
                <div className="text-[10px] text-muted-foreground">{label} d</div>
                <div className="text-[10px] break-words leading-tight">{formatCurrency(aging.payable?.[k]?.value || 0)}</div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* === ROW 5b — TREND CHARTS === */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card testid="chart-cashflow">
          <SectionTitle icon={ChartLineUp}>Cash Flow Trend (6 months) <span className="text-[10px] font-normal text-muted-foreground ml-2">click to drill</span></SectionTitle>
          <CashFlowTrendChart
            data={trend6m}
            formatCurrency={formatCurrency}
            onBarClick={(point) => {
              const r = monthRange(point.label);
              openDrill({ category: 'cash', title: `Cash & Bank movements — ${point.name}`, startDate: r.from, endDate: lastDay(r.to) });
            }}
          />
        </Card>
        <Card testid="chart-pnl">
          <SectionTitle icon={ChartBar}>Revenue vs Expenses (6 months) <span className="text-[10px] font-normal text-muted-foreground ml-2">click to drill</span></SectionTitle>
          <PnLTrendChart
            data={trend6m}
            formatCurrency={formatCurrency}
            onBarClick={(point, _, series) => {
              const r = monthRange(point.label);
              openDrill({ category: series === 'Revenue' ? 'income' : 'expense', title: `${series} — ${point.name}`, startDate: r.from, endDate: lastDay(r.to) });
            }}
          />
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card testid="chart-funnel">
          <SectionTitle icon={ChartBar}>Sales Funnel <span className="text-[10px] font-normal text-muted-foreground ml-2">click bar</span></SectionTitle>
          <SalesPipelineFunnelChart
            buckets={sp.buckets}
            onBarClick={(point) => setDrilldown({
              type: 'quotation',
              title: `Quotes — ${point.name}`,
              filter: { status: point.statusKey },
            })}
          />
        </Card>
        <Card testid="chart-quotes-invoices">
          <SectionTitle icon={ChartLineUp}>Quotes & Invoices Issued <span className="text-[10px] font-normal text-muted-foreground ml-2">click point</span></SectionTitle>
          <QuotesInvoicesTrendChart
            data={trend6m}
            onPointClick={(point, _, series) => {
              const r = monthRange(point.label);
              setDrilldown({
                type: series === 'Quotes' ? 'quotation' : 'invoice',
                title: `${series} — ${point.name}`,
                filter: r,
              });
            }}
          />
        </Card>
        <Card testid="chart-expenses-pie">
          <SectionTitle icon={ChartPie}>Expense Distribution (this month) <span className="text-[10px] font-normal text-muted-foreground ml-2">click slice</span></SectionTitle>
          <ExpensesPieChart
            data={mf.top_expenses}
            formatCurrency={formatCurrency}
            onSliceClick={(slice) => drillMonth('expense', slice.name, { accountId: slice.account_id })}
          />
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card testid="chart-ar-aging">
          <SectionTitle icon={ChartBar}>Receivables Aging <span className="text-[10px] font-normal text-muted-foreground ml-2">click bar</span></SectionTitle>
          <AgingBarChart
            buckets={aging.receivable}
            color="#ef4444"
            onBarClick={() => openParty('customer')}
          />
        </Card>
        <Card testid="chart-ap-aging">
          <SectionTitle icon={ChartBar}>Payables Aging</SectionTitle>
          <AgingBarChart buckets={aging.payable} color="#f59e0b" onBarClick={() => openParty('supplier')} />
        </Card>
        <Card testid="chart-top-customers">
          <SectionTitle icon={ChartBar}>Top Customers (Visual)</SectionTitle>
          <TopCustomersBarChart data={tp.customers} formatCurrency={formatCurrency} />
        </Card>
      </div>

      {/* === ROW 6 — Top customers / suppliers + Activity === */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card testid="top-customers" to="/dashboard/contacts?type=customer">
          <SectionTitle icon={Crown}>Top Customers</SectionTitle>
          {(tp.customers || []).length === 0 ? (
            <p className="text-xs text-muted-foreground">No invoices yet</p>
          ) : (
            <ul className="space-y-2 text-sm">
              {tp.customers.map((c, i) => (
                <li key={i} className="flex justify-between border-b last:border-0 pb-1">
                  <span className="truncate">{c.name}</span>
                  <span className="font-bold">{formatCurrency(c.total)}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
        <Card testid="top-suppliers" to="/dashboard/contacts?type=supplier">
          <SectionTitle icon={Crown}>Top Suppliers</SectionTitle>
          {(tp.suppliers || []).length === 0 ? (
            <p className="text-xs text-muted-foreground">No bills yet</p>
          ) : (
            <ul className="space-y-2 text-sm">
              {tp.suppliers.map((s, i) => (
                <li key={i} className="flex justify-between border-b last:border-0 pb-1">
                  <span className="truncate">{s.name}</span>
                  <span className="font-bold">{formatCurrency(s.total)}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
        <Card testid="recent-activity">
          <SectionTitle icon={ClockCounterClockwise}>Recent Activity (14d)</SectionTitle>
          {(d.recent_activity || []).length === 0 ? (
            <p className="text-xs text-muted-foreground">No recent activity</p>
          ) : (
            <ul className="space-y-1.5 text-xs">
              {d.recent_activity.slice(0, 8).map((a, i) => (
                <li key={i} className="flex justify-between gap-2 py-1 border-b last:border-0">
                  <div className="truncate">
                    <span className="font-semibold">{a.number}</span>
                    <span className="text-muted-foreground"> · {a.type?.replace(/_/g, ' ')}</span>
                    {a.party && <span className="text-muted-foreground"> · {a.party}</span>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] ${STATUS_TONE[a.status] || 'bg-muted/30'}`}>{a.status}</span>
                    <span className="font-semibold">{formatCurrency(a.amount)}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      {dialogs}
      {/* Drilldown Modal */}
      <DrilldownModal
        open={!!drilldown}
        onClose={() => setDrilldown(null)}
        type={drilldown?.type}
        title={drilldown?.title}
        filter={drilldown?.filter}
      />
    </div>
  );
}
